/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { getRouteMap, ROUTES } from "Router";
import calculateOffset from "utils/calculateOffset";

import {
  getNotifications,
  setNotificationAsRead,
} from "lib/v2/notificationsApi";

const notificationsQueryKeys = {
  page: (page) => ["notifications", page],
};

const userRouteMap = getRouteMap(ROUTES.USERS);
const applicationRouteMap = getRouteMap(ROUTES.APPLICATIONS);

const NOTIFICATIONS_ACTIONS = {
  invitation_response: (userId) => userRouteMap.invitation(userId),
  trip_approved: (userId, tripId) =>
    userRouteMap.tripDetails.edit(userId, tripId),
  user_flight: (userId, tripId, flightId) =>
    userRouteMap.generalFlights.edit(userId, flightId),
  application: () => applicationRouteMap.base,
};

const TYPES = {
  invitation_response: "invitation_response",
  trip_approved: "trip_approved",
  user_flight: "user_flight",
  application: "application",
};

const useNotifications = (page = 1) => {
  const offset = useMemo(() => calculateOffset(page), [page]);
  const navigate = useNavigate();

  const { data, isFetching, isFetched, isSuccess } = useQuery(
    notificationsQueryKeys.page(page),
    () => getNotifications(offset),
    {
      // This will be used to manage tracking app_version changes
      staleTime: 0,
      cacheTime: 1 * 60 * 1000, // 1 minutes
      refetchOnWindowFocus: "always",
      onError: (error) =>
        console.error("Failed to fetch notifications:", error),
    },
  );

  const handleTapToView = useCallback(
    async (notification) => {
      const action = NOTIFICATIONS_ACTIONS[notification.action];
      const type = TYPES[notification.action];
      const [userId] = notification.users || [];
      const [tripId] = notification.trips || [];
      const [flightId] = notification.hayya || [];

      if (!action) {
        console.warn("No action defined for notification type:", type);
        return;
      }

      try {
        await setNotificationAsRead({
          notificationId: notification.id,
          eventId: notification.event_id,
        });
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }

      const goTo =
        type === TYPES.invitation_response
          ? action(userId)
          : type === TYPES.trip_approved
            ? action(userId, tripId)
            : type === TYPES.user_flight
              ? action(userId, tripId, flightId)
              : type === TYPES.application
                ? action()
                : null;

      if (goTo) navigate(goTo);
    },
    [navigate],
  );

  return {
    data,
    isFetching,
    isFetched,
    isSuccess,
    handleTapToView,
  };
};

export default useNotifications;
